import { Alert, Stack, Typography } from '@mui/joy';
import { Warning, X } from '@phosphor-icons/react';
import { SelectionChangedEvent } from 'ag-grid-community';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Notification, SlidingPanel, SlidingPanelContent } from 'core/components';
import { Tooltip } from 'core/elements';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { INotification } from 'core/types';
import { useCostDecimals } from 'pricing/hooks';
import { TradeAgreementUsage } from 'quotes/enums';
import { useDropFailedTradeAgreements, useGetTradeAgreements } from 'quotes/hooks';
import { ISelectedSpaFormValue, ITradeAgreement, TTradeAgreementsRequest } from 'quotes/types';
import { isTradeAgreementExpired } from 'quotes/utils';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { SpaListTable } from './SpaListTable';
import {
  getSelectedSpaNoLongerOfferedMessage,
  getTradeAgreementUnavailableKey,
  getTradeAgreementUsage,
  getTradeAgreementUsageContext,
  getTransientLine,
  IDefaultSpaAgreement,
  isTradeAgreementsLineError,
  withTradeAgreementsLineMessage,
} from '../utils';

export interface ISelectSpaCostPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onUse: (agreement: ITradeAgreement) => Promise<void> | void;
  onRemove: () => Promise<void> | void;
  onHeldAgreementOffered?: (isOffered: boolean) => void;
  partNumber?: string;
  // Frozen on open: a background recalculation must not refetch and drop the row about to be used.
  requestPayload: TTradeAgreementsRequest | null;
  selectedSpa?: ISelectedSpaFormValue | null;
  // The line's default SPA: choosing it while a pick is held removes the pick.
  defaultSpa?: IDefaultSpaAgreement | null;
  // Set while the pricing engine is paused: shown instead of the list, which is not asked for.
  unavailableMessage?: string | null;
  level?: number;
}

const renderWarning = (message: string) => (
  <Alert
    startDecorator={<Warning style={{ width: 24, height: 24 }} />}
    variant={Variant.SOFT}
    color={Color.WARNING}
    size={Size.LG}
    sx={{
      alignItems: 'flex-start',
      width: '100%',

      '& .MuiAlert-startDecorator': {
        marginTop: '2px',
      },
    }}
  >
    <Typography level={Level.BODY_SM}>{message}</Typography>
  </Alert>
);

// Must render under its own NotificationProvider: use SelectSpaCostPanelWithProvider.
export const SelectSpaCostPanel = ({
  isOpen,
  onClose,
  onUse,
  onRemove,
  onHeldAgreementOffered,
  partNumber,
  requestPayload,
  selectedSpa,
  defaultSpa,
  unavailableMessage = null,
  level = 1,
}: ISelectSpaCostPanelProps) => {
  const [selectedAgreement, setSelectedAgreement] = useState<ITradeAgreement | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  // The inline action stays enabled while submitting, so a second click must not run it again.
  const isSubmittingRef = useRef(false);
  // Each failed lookup is announced once, and only its own notification is cleared.
  const announcedFailureRef = useRef<{
    failure: unknown;
    notification: INotification;
    updatedAt?: number;
  } | null>(null);
  // For the close: the host has taken the request back by then.
  const openedRequestRef = useRef<TTradeAgreementsRequest | null>(null);

  const { t } = useTranslation();

  const costDecimals = useCostDecimals();

  const {
    clearNotification,
    getNotification,
    showErrorNotification,
    showLoadingNotification,
    showNotification,
  } = useNotificationContext();

  const dropFailedTradeAgreements = useDropFailedTradeAgreements();

  const isUnavailable = !!unavailableMessage;

  const {
    lines,
    dataUpdatedAt,
    error,
    errorUpdatedAt,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch,
  } = useGetTradeAgreements(requestPayload, isOpen && !!requestPayload && !isUnavailable);

  const line = getTransientLine(lines);
  const agreements = line?.tradeAgreements ?? [];
  const unavailableKey = getTradeAgreementUnavailableKey(line);
  const heldAgreementCode = selectedSpa?.spaCostSource;
  const { defaultRowId, heldRowId, isDefaultInUse } = getTradeAgreementUsageContext(
    agreements,
    selectedSpa,
    defaultSpa
  );
  const hasFailedAnswer = isError || isTradeAgreementsLineError(line);
  // React Query hands back a cached failed answer while its replacement is in flight.
  const hasLoadFailed = hasFailedAnswer && !isFetching;
  const isListLoading = isLoading || (isFetching && hasFailedAnswer);
  // Only a loaded list can say the pick is gone.
  const isHeldAgreementUnlisted =
    !isUnavailable && !!heldAgreementCode && !isLoading && !hasFailedAnswer && null === heldRowId;

  // A failure's details are in the error notification; otherwise the provider's message wins.
  const noRowsOverlayMessage = hasLoadFailed
    ? t('errorTitleTradeAgreementsFetch')
    : t('messageNoTradeAgreementsAvailable');
  const noRowsOverlayReason = hasFailedAnswer
    ? null
    : line?.message || (unavailableKey ? t(unavailableKey) : null);

  const noLongerOfferedMessage = isHeldAgreementUnlisted
    ? getSelectedSpaNoLongerOfferedMessage(selectedSpa, costDecimals)
    : null;

  // Closed, the panel stays mounted on a disabled, empty query.
  useEffect(() => {
    if (!isOpen || isUnavailable || !heldAgreementCode || !isSuccess || hasFailedAnswer) {
      return;
    }

    onHeldAgreementOffered?.(null !== heldRowId);
  }, [
    hasFailedAnswer,
    heldAgreementCode,
    heldRowId,
    isOpen,
    isSuccess,
    isUnavailable,
    onHeldAgreementOffered,
  ]);

  const onRefresh = useCallback(() => {
    clearNotification();
    refetch();
  }, [clearNotification, refetch]);

  const onSelectionChanged = useCallback(({ api }: SelectionChangedEvent<ITradeAgreement>) => {
    setSelectedAgreement(api?.getSelectedNodes()?.[0]?.data ?? null);
  }, []);

  // The panel stays mounted while closed, so a stale message would greet the next opening.
  const closePanel = useCallback(() => {
    clearNotification();
    setSelectedAgreement(null);
    setTimeout(() => onClose());
  }, [clearNotification, onClose]);

  // The list unmounts meanwhile, so a selection kept here would be invisible when it comes back.
  useEffect(() => {
    if (isUnavailable) {
      setSelectedAgreement(null);
    }
  }, [isUnavailable]);

  // Drops a failed answer so the next opening reloads; while open it would reload at once.
  useEffect(() => {
    if (isOpen) {
      openedRequestRef.current = requestPayload;

      return;
    }

    clearNotification();

    if (openedRequestRef.current) {
      dropFailedTradeAgreements(openedRequestRef.current);
      openedRequestRef.current = null;
    }
  }, [clearNotification, dropFailedTradeAgreements, isOpen, requestPayload]);

  // A failure keeps the panel open, its error replacing the loading message in the one slot.
  const runAndClose = useCallback(
    async (action: () => Promise<void> | void, loadingMessage?: string) => {
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;
      setSubmitting(true);

      if (loadingMessage) {
        showLoadingNotification({ message: loadingMessage }, NotificationPosition.DRAWER);
      }

      try {
        await action();
      } catch (error) {
        showErrorNotification({}, NotificationPosition.DRAWER, error as AxiosError);

        return;
      } finally {
        isSubmittingRef.current = false;
        setSubmitting(false);
      }

      closePanel();
    },
    [closePanel, showErrorNotification, showLoadingNotification]
  );

  // Nothing is persisted here: the host form's Save commits the pick.
  const applyAgreement = useCallback(
    async (agreement: ITradeAgreement) => {
      const usage = getTradeAgreementUsage(agreement, { defaultRowId, heldRowId, isDefaultInUse });

      if (
        isUnavailable ||
        !agreement ||
        TradeAgreementUsage.IN_USE === usage ||
        TradeAgreementUsage.DEFAULT_IN_USE === usage ||
        isTradeAgreementExpired(agreement)
      ) {
        return;
      }

      // The default is no pick: choosing it removes the one held.
      await runAndClose(
        TradeAgreementUsage.DEFAULT === usage ? onRemove : () => onUse(agreement),
        t('messageSelectedSpaUseInProgress', { agreementCode: agreement.spaCostSource })
      );
    },
    [defaultRowId, heldRowId, isDefaultInUse, isUnavailable, onRemove, onUse, runAndClose, t]
  );

  // The default the line is on is never a grid selection, yet its radio stays checked.
  const chosenAgreementUsage =
    !selectedAgreement && isDefaultInUse && !!defaultRowId && !hasFailedAnswer
      ? TradeAgreementUsage.DEFAULT_IN_USE
      : getTradeAgreementUsage(selectedAgreement, { defaultRowId, heldRowId, isDefaultInUse });
  const useDisabledReason = [
    TradeAgreementUsage.DEFAULT_IN_USE,
    TradeAgreementUsage.IN_USE,
  ].includes(chosenAgreementUsage)
    ? t('tooltipSpaAlreadyInUse')
    : null;
  const isUseDisabled = isUnavailable || isSubmitting || !selectedAgreement || !!useDisabledReason;

  const buttons = (
    <>
      <Stack direction={Direction.ROW} spacing={0.5} alignItems="center">
        <Tooltip title={useDisabledReason ?? ''}>
          <Button
            text={t('buttonLabelUseThisSpa')}
            onClick={() => applyAgreement(selectedAgreement)}
            disabled={isUseDisabled}
          />
        </Tooltip>

        {!!heldAgreementCode && (
          <Button
            text={t('buttonLabelRemoveSelectedSpa')}
            variant={Variant.SOFT}
            color={Color.DANGER}
            onClick={() => runAndClose(onRemove)}
            disabled={isUnavailable || isSubmitting}
          />
        )}
      </Stack>

      <Button
        text={t('buttonLabelCancel')}
        color={Color.NEUTRAL}
        variant={Variant.OUTLINED}
        startDecorator={<X />}
        onClick={closePanel}
        disabled={isSubmitting}
      />
    </>
  );

  // A Refresh failing the same way returns the same `line_error` line (structural sharing).
  const failureUpdatedAt = isError ? errorUpdatedAt : dataUpdatedAt;

  // Announced once its request has settled; taken back when the list loads again.
  useEffect(() => {
    const announced = announcedFailureRef.current;

    // While unavailable the alert says why; an earlier refusal must not repeat it.
    if (!isOpen || !hasLoadFailed || isUnavailable) {
      // A failed Use or Remove and the table's Save Layout share the panel's one slot.
      if (announced && getNotification() === announced.notification) {
        clearNotification();
      }

      announcedFailureRef.current = null;

      return;
    }

    const failure = error ?? line;

    if (announced?.failure === failure && announced?.updatedAt === failureUpdatedAt) {
      return;
    }

    // Built here so it can be recognised when it has to be cleared.
    const notification: INotification = {
      title: t('errorTitleTradeAgreementsFetch'),
      message: isError
        ? (error as AxiosError<{ error?: string }>)?.response?.data?.error || t('serverError')
        : withTradeAgreementsLineMessage(t('messageTradeAgreementsLoadError'), line),
      type: ApiStatus.ERROR,
      position: NotificationPosition.DRAWER,
      disableAutoClose: true,
    };

    announcedFailureRef.current = { failure, notification, updatedAt: failureUpdatedAt };

    showNotification(notification);
  }, [
    clearNotification,
    error,
    failureUpdatedAt,
    getNotification,
    hasLoadFailed,
    isError,
    isOpen,
    isUnavailable,
    line,
    showNotification,
    t,
  ]);

  return (
    // X and Escape wait for a running action, whose outcome is reported in the panel.
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={closePanel}
      title={t('titleSelectSpaCost')}
      subtitle={partNumber}
      buttons={buttons}
      level={level}
      disableClose={isSubmitting}
      disableAutoClose={isSubmitting}
      autoWidth
    >
      <SlidingPanelContent withoutNotification>
        {/* TOP carries the table's own toasts (Save Layout), which would go nowhere else. */}
        <Notification position={NotificationPosition.DRAWER} />
        <Notification position={NotificationPosition.TOP} />

        {noLongerOfferedMessage &&
          renderWarning(t(noLongerOfferedMessage.key, noLongerOfferedMessage.values))}

        {isUnavailable && renderWarning(unavailableMessage)}

        {/* Otherwise always the table, so the drawer never reads as a blank page. */}
        {!isUnavailable && (
          <SpaListTable
            agreements={hasFailedAnswer ? [] : agreements}
            heldRowId={heldRowId}
            defaultRowId={defaultRowId}
            noRowsMessage={noRowsOverlayMessage}
            noRowsReason={noRowsOverlayReason}
            onSelectionChanged={onSelectionChanged}
            onUse={applyAgreement}
            onRefresh={hasFailedAnswer ? onRefresh : undefined}
            isLoading={isListLoading}
            isRefreshing={isFetching}
            isDefaultInUse={isDefaultInUse}
          />
        )}
      </SlidingPanelContent>
    </SlidingPanel>
  );
};
